import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', href: '/homepage' },
    { label: 'Services', href: '/services-overview' },
    { label: 'Reviews', href: '/customer-reviews' },
  ];

  const serviceLinks = [
    { label: 'Get Quote', href: '/get-quote' },
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'ShareIcon', href: '#' },
    { name: 'Twitter', icon: 'ChatBubbleLeftIcon', href: '#' },
    { name: 'Instagram', icon: 'PhotoIcon', href: '#' },
  ];

  return (
    <footer className={`bg-surface border-t border-border ${className}`}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div className="space-y-4">
            <Link href="/homepage" className="inline-flex items-center space-x-3 transition-smooth hover:opacity-80">
              <AppImage
                src="/assets/images/b37fa040-c4d5-47fb-ac00-191e53f206e6-1769810300759.JPG"
                alt="Crystal Clean Logo"
                width={120}
                height={40}
                className="h-10 w-auto object-contain"
              />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Professional exterior cleaning services for discerning homeowners who value quality and reliability.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="p-2 bg-accent hover:bg-primary hover:text-white rounded-full transition-smooth btn-bubbly"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-serif text-lg font-semibold text-foreground mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-smooth inline-flex items-center group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="mr-1 group-hover:translate-x-1 transition-smooth" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-serif text-lg font-semibold text-foreground mb-4">Services</h3>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-smooth inline-flex items-center group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="mr-1 group-hover:translate-x-1 transition-smooth" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-serif text-lg font-semibold text-foreground mb-4">Contact Info</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3 text-sm text-muted-foreground">
                <Icon name="PhoneIcon" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                <a href="tel:2368672004" className="text-blue-600 hover:text-blue-700 font-semibold transition-smooth">(236) 867-2004</a>
              </li>
              <li className="flex items-start space-x-3 text-sm text-muted-foreground">
                <Icon name="EnvelopeIcon" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                <span>crystalcleanextr@gmail.com</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-muted-foreground">
              © {currentYear} CrystalClean. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link href="#" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                Privacy Policy
              </Link>
              <Link href="#" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                Terms of Service
              </Link>
              <div className="flex items-center space-x-2">
                <Icon name="ShieldCheckIcon" size={18} className="text-success" />
                <span className="text-sm text-muted-foreground">Fully Insured</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;